import service from '../utility/service';
import { errorOccurred } from '../errors/reducer';
import { getDefaultTimezone } from '../utility/date';
import { decodeError } from '../utility/errors';

const INITIAL_STATE = {
  isin: null,
  product: null,
  isinErrorMessage: null,
  fetching: false,
  unsavedChanges: false,
  fieldsInstrumentTypeId: undefined,
  fields: [],
  requiresLookup: false,
  fetchingFields: false
};

const types = {
  RECEIVED_RECORD: 'orb/isinDetail/RECEIVED_RECORD',
  FETCHING_PRODUCT: 'orb/isinDetail/FETCHING_PRODUCT',
  FETCHING_ISIN: 'orb/isinDetail/FETCHING_ISIN',
  RECEIVED_PRODUCT: 'orb/isinDetail/RECEIVED_PRODUCT',
  FAILED_ISIN: 'orb/isinDetail/FAILED_ISIN',
  FAILED_PRODUCT: 'orb/isinDetail/FAILED_PRODUCT',
  NEW_RECORD: 'orb/isinDetail/NEW_RECORD',
  SET_PRODUCT_PROPERTY: 'orb/isinDetail/SET_PRODUCT_PROPERTY',
  SET_PRODUCT_STARTING_VALUES: 'orb/isinDetail/SET_PRODUCT_STARTING_VALUES',
  CLEAR_PRODUCT: 'orb/isinDetil/CLEAR_PRODUCT',
  FETCHING_FIELDS: 'orb/isinDetail/FETCHING_FIELDS',
  RECEIVED_FIELDS: 'orb/isinDetail/RECEIVED_FIELDS',
  DEFAULT_RECORD_PROPERTY: 'orb/isinDetail/DEFAULT_RECORD_PROPERTY',
  FETCHING_TERM: 'orb/isinDetail/FETCHING_TERM',
  RECEIVED_TERM: 'orb/isinDetail/RECEIVED_TERM',
  FAILED_TERM: 'orb/isinDetail/FAILED_TERM',
};

const dependencies = {
  InstrumentTypeId: [
    'BaseProduct', 'FinalPriceType', 'BaseProduct2',
    'ReferenceRate', 'ReferenceRate2', 'TransactionType',
    'UnderlyingAssetType', 'UnderlyingInstrumentIndex', 'ValuationMethodorTrigger',
    'SubProduct', 'AdditionalSubProduct', 'OtherSubProduct', 'OtherAdditionalSubProduct',
    'NotionalCcy', 'NotionalCcy2', 'SettlementCurrency', 'CommodityShortcut'
  ],
  CommodityShortcut: [
    'SubProduct', 'AdditionalSubProduct'
  ],
  BaseProduct: [
    'SubProduct', 'AdditionalSubProduct'
  ],
  SubProduct: [
    'AdditionalSubProduct'
  ],
  BaseProduct2: [
    'OtherSubProduct', 'OtherAdditionalSubProduct'
  ],
  OtherSubProduct: [
    'OtherAdditionalSubProduct'
  ]
};

const fixedStartingValues = {
  43: {
    DeliveryType: 'Cash',
    ReturnorPayoutTrigger: 'Contract for Difference (CFD)',
    TransactionType: 'SWAP'
  },
  44: {
    DeliveryType: 'Cash',
    ReturnorPayoutTrigger: 'Contract for Difference (CFD)',
    TransactionType: 'SWAP'
  },
  45: {
    DeliveryType: 'Cash',
    ReturnorPayoutTrigger: 'Contract for Difference (CFD)',
    TransactionType: 'SWAP'
  },
  77: {
    PriceMultiplier: 1
  }
};

const frontOfficeSystems = [
  { name: 'Sophis', instruments: [49, 50, 47, 46, 48, 44, 45, 43] },
  { name: 'Bloomberg', instruments: [52, 62] },
  { name: 'Particle', instruments: [40, 41, 73, 31, 72, 33, 77, 25, 39, 34, 35, 58] },
  { name: 'Murex',
    instruments: [74, 42, 32, 75, 76, 71, 30,
      61, 51, 55, 56, 23, 24, 22, 21, 36, 38,
      59, 57, 35, 37, 26, 27, 28, 29]
  }
];

export default function reducer(state = INITIAL_STATE, action = {}) {
  switch (action.type) {
    case types.FETCHING_PRODUCT:
      return Object.assign({}, state, {
        fetching: true,
        isin: action.isin,
        isinErrorMessage: null
      });
    case types.FETCHING_ISIN:
      return Object.assign({}, state, {
        fetching: true,
        isin: null,
        product: action.product,
        isinErrorMessage: null
      });
    case types.RECEIVED_PRODUCT:
      return Object.assign({}, state, {
        fetching: false,
        isin: action.product.Isin,
        product: action.product,
        unsavedChanges: false,
        isinErrorMessage: null
      });

    case types.NEW_RECORD:
      return Object.assign({}, state, {
        fetching: false,
        isin: null,
        product: {},
        unsavedChanges: false,
        fieldsInstrumentTypeId: null,
        fields: []
      });
    case types.RECEIVED_RECORD:
      return Object.assign({}, state, {
        isinErrorMessage: null
      });
    case types.SET_PRODUCT_PROPERTY:
    {
      let localAction = { ...action };
      let product = Object.assign({}, state.product);

      // convert the shortcut selection into the first in the chain BaseProduct
      if (action.propertyName === 'CommodityShortcut' && action.value) {
        const shortcutElements = action.value.split(':');
        const len = shortcutElements.length;

        localAction = { value: shortcutElements[0], propertyName: 'BaseProduct' };
        const sub = len > 1 ? shortcutElements[1] : undefined;
        const addSub = len > 2 ? shortcutElements[2] : undefined;
        product = Object.assign({}, state.product, { BaseProduct: localAction.value, SubProduct: sub, AdditionalSubProduct: addSub });
      }

      if (dependencies[localAction.propertyName] && (product[localAction.propertyName] !== localAction.value)) {
        // Clear dependent properties
        dependencies[localAction.propertyName].forEach((prop) => {
          if (product[prop]) delete product[prop];
        });
      }
      product[localAction.propertyName] = localAction.value;
      return Object.assign({}, state, {
        product,
        unsavedChanges: true
      });
    }
    case types.CLEAR_PRODUCT:
    {
      const {
        InstrumentTypeId,
        ProductId,
        isin
      } = state.product;
      // Reset the product object but preserve the key properties
      const product = {
        InstrumentTypeId,
        ProductId,
        isin
      };
      return Object.assign({}, state, {
        product
      });
    }
    case types.SET_PRODUCT_STARTING_VALUES:
    {
      const updatedProduct = Object.assign({}, state.product, action.startingValues);
      return Object.assign({}, state, {
        product: updatedProduct
      });
    }
    case types.FETCHING_FIELDS:
      return Object.assign({}, state, {
        fetchingFields: true,
        fields: []
      });
    case types.RECEIVED_FIELDS:return Object.assign({}, state, {
      fetchingFields: false,
      fieldsInstrumentTypeId: action.instrumentTypeId,
      fields: action.fields,
      requiresLookup: action.requiresLookup
    });
    case types.FAILED_ISIN:
      return Object.assign({}, state, {
        fetching: false
      });
    case types.FAILED_PRODUCT:
      return Object.assign({}, state, {
        fetching: false,
        product: action.productId ? { ProductId: action.productId } : state.product,
        isinErrorMessage: action.message
      });

    case types.FETCHING_TERM:
      return Object.assign({}, state, {
        fetching: true,
        isinErrorMessage: null
      });

    case types.RECEIVED_TERM: {
      const product = { ...state.product,
        TermofContractValue: action.product.TermofContractValue,
        TermofContractUnit: action.product.TermofContractUnit
      };

      return Object.assign({}, state, {
        fetching: false,
        product,
        unsavedChanges: true,
        isinErrorMessage: null
      });
    }
    case types.FAILED_TERM:
      return Object.assign({}, state, {
        fetching: false,
        isinErrorMessage: action.message
      });
    default:
      return state;
  }
}
reducer.types = types;
reducer.initialState = INITIAL_STATE;
reducer.startingValues = fixedStartingValues;

function fetchingProduct(isin) {
  return {
    type: types.FETCHING_PRODUCT,
    isin
  };
}

function fetchingIsin(product) {
  return {
    type: types.FETCHING_ISIN,
    product
  };
}

function receivedProduct(product) {
  return {
    type: types.RECEIVED_PRODUCT,
    product
  };
}

function fetchingFields() {
  return {
    type: types.FETCHING_FIELDS
  };
}

function receivedFields(instrumentTypeId, fields, requiresLookup) {
  return {
    type: types.RECEIVED_FIELDS,
    instrumentTypeId,
    fields,
    requiresLookup
  };
}

export function receivedRecord() {
  return {
    type: types.RECEIVED_RECORD
  };
}

export function newRecord() {
  return {
    type: types.NEW_RECORD
  };
}

function failedIsin() {
  return {
    type: types.FAILED_ISIN
  };
}

function failedProduct(error, productId) {
  const detail = decodeError(error);

  return {
    type: types.FAILED_PRODUCT,
    message: detail.message,
    productId
  };
}

function shouldFetchProduct(state, productId, editing) {
  return (!state.product || state.product.ProductId !== productId
      || (!editing && state.unsavedChanges))
      && !state.fetching;
}

export function fetchProductIfNeeded(productId) {
  return (dispatch, getState) => {
    const state = getState();
    if (shouldFetchProduct(state.isinDetail, productId, state.recordDetail.editing)) {
      return dispatch(fetchProduct(productId));
    }
    return Promise.resolve();
  };
}

export function fetchProduct(productId) {
  return (dispatch) => {
    dispatch(fetchingProduct());

    if (productId) {
      return service.get(`/Products/${productId}`).then((response) => {
        dispatch(receivedProduct(response.data));
      }).catch((err) => {
        dispatch(failedProduct(err, productId));
      });
    }
    // Started editing a new one
    dispatch(receivedProduct({}));
    return Promise.resolve();
  };
}

function shouldFetchProductForIsin(state, isin) {
  return isin !== state.isin;
}

export function fetchProductForIsinIfNeeded(isin) {
  return (dispatch, getState) => {
    const state = getState();
    if (shouldFetchProductForIsin(state.isinDetail, isin)) {
      return dispatch(fetchProductForIsin(isin));
    }
    return Promise.resolve();
  };
}

export function fetchProductForIsin(isin) {
  return (dispatch) => {
    dispatch(fetchingProduct(isin));

    if (isin && isin.length) {
      return service.get(`/IsinProducts/${isin}`).then((response) => {
        dispatch(receivedProduct(response.data));
      }).catch((err) => {
        dispatch(failedProduct(err));
      });
    }
    // Started editing a new one
    dispatch(receivedProduct({}));
    return Promise.resolve();
  };
}

function shouldFetchIsin(state) {
  return state.unsavedChanges && !state.fetching;
}

export function fetchIsinIfChanged(instrumentTypeId) {
  return (dispatch, getState) => {
    const state = getState().isinDetail;
    if (shouldFetchIsin(state)) {
      return fetchIsin(dispatch, instrumentTypeId, state.product, getState().staticData.instrumentTypes);
    }
    return Promise.resolve(true);
  };
}

function fetchIsin(dispatch, instrumentTypeId, product, instrumentTypes) {
  dispatch(fetchingIsin(product));

  return service.post(`/ProductDefinitions/${instrumentTypeId}`, product).then((response) => {
    dispatch(receivedProduct(response.data));
    return true;
  }).catch((err) => {
    const isFxSwap = instrumentTypes.find(i => i.InstrumentTypeId === product.InstrumentTypeId && i.Name.endsWith('FX Swap')) !== undefined;

    if (isFxSwap === true && err.response !== undefined) {
      let data = err.response.data;

      data = data.replace('Other Notional Currency', 'Reference Currency');
      err.response.data = data.replace('Notional Currency', 'Base Currency'); // eslint-disable-line no-param-reassign
    }

    dispatch(errorOccurred(err, `Getting ISIN for Instrument Id ${instrumentTypeId}`));
    dispatch(failedIsin());
    return false;
  });
}

export function setProductProperty(propertyName, value) {
  return (dispatch, getState) => {
    dispatch({
      type: types.SET_PRODUCT_PROPERTY,
      propertyName,
      value
    });

    if (propertyName === 'InstrumentTypeId') {
      const fontOfficeDefault = frontOfficeSystems.find(
        f => f.instruments.find(id => value === id)
      );
      const frontOfficeList = getState().staticData.tradingSystems;

      const frontOffice = (fontOfficeDefault && frontOfficeList)
        ? frontOfficeList.find(office => office.Name === fontOfficeDefault.name)
        : null;

      dispatch(defaultRecordProperty('FrontOfficeSystem', frontOffice));
    }
  };
}

function defaultRecordProperty(propertyName, value) {
  return {
    type: types.DEFAULT_RECORD_PROPERTY,
    propertyName,
    value
  };
}

function setProductStartingValues(startingValues) {
  return {
    type: types.SET_PRODUCT_STARTING_VALUES,
    startingValues
  };
}

function updateStartingValues(fieldsData) {
  return (dispatch) => {
    if (!fieldsData) {
      return;
    }
    dispatch(setProductStartingValues(fieldsData));
  };
}

function clearProduct() {
  return {
    type: types.CLEAR_PRODUCT
  };
}

function shouldFetchFields(state, instrumentTypeId) {
  return state.fieldsInstrumentTypeId !== instrumentTypeId
      && !state.fetchingFields;
}

export function fetchFieldsIfNeeded(instrumentTypeId) {
  return (dispatch, getState) => {
    const appState = getState();
    const state = appState.isinDetail;
    const resetProduct = appState.recordDetail.editing && state.unsavedChanges;

    if (shouldFetchFields(state, instrumentTypeId)) {
      dispatch(fetchingFields());
      if (resetProduct) {
        // Clear the current product before fetching new fields
        dispatch(clearProduct());
      }

      if (instrumentTypeId && productRequiresLookup(appState.staticData, instrumentTypeId)) {
        return service.get(`/ProductFields/${instrumentTypeId}`).then((response) => {
          dispatch(receivedFields(instrumentTypeId, response.data, true));

          if (resetProduct) {
            // TODO Change this to set the starting values of the response.data once the endpoint is configured
            dispatch(updateStartingValues(fixedStartingValues[instrumentTypeId]));
          }
        }).catch((err) => {
          dispatch(errorOccurred(err, `Getting fields for Instrument Id ${instrumentTypeId}`));
          dispatch(receivedFields(instrumentTypeId, [], true));
        });
      }
      const noLookupFields = instrumentTypeId ? [{ ProductFieldName: 'Isin', Mandatory: false }] : [];
      dispatch(receivedFields(instrumentTypeId, noLookupFields, false));
    }
    return Promise.resolve();
  };
}

function productRequiresLookup(staticState, instrumentId) {
  const instrumentType = staticState.instrumentTypes
    .find(i => i.InstrumentTypeId === instrumentId);

  return instrumentType && instrumentType.RequiresLookup;
}

export function setCurrentTime(timePropertyName, timeZoneName) {
  return (dispatch) => {
    const zone = timeZoneName || getDefaultTimezone();
    return service.get(`/DateTime?timezone=${zone}`).then((response) => {
      const date = response.data.Date;
      dispatch(setProductProperty(timePropertyName, date));
    }).catch((err) => {
      dispatch(errorOccurred(err, `Getting current time in ${zone}`));
    });
  };
}

export function fetchingTerm(startDate, endDate) {
  return {
    type: types.FETCHING_TERM,
    startDate,
    endDate
  };
}

function receivedTerm(product) {
  return {
    type: types.RECEIVED_TERM,
    product
  };
}

function failedTerm(error) {
  const detail = decodeError(error);

  return {
    type: types.FAILED_TERM,
    message: detail.message
  };
}

export function fetchDerivedTermOfContract(startDate, endDate) {
  return (dispatch) => {
    dispatch(fetchingTerm());
    const sd = startDate.replace(/\//g, '-');
    const ed = endDate.replace(/\//g, '-');

    if (startDate && endDate) {
      return service.get(`/Terms/${sd}/${ed}`).then((response) => {
        dispatch(receivedTerm(response.data));
      }).catch((err) => {
        dispatch(failedTerm(err));
      });
    }
    // Started editing a new one
    dispatch(receivedTerm({}));
    return Promise.resolve();
  };
}



// WEBPACK FOOTER //
// ./src/isinDetail/reducer.js